<template>
  <b-modal
    :id="'upload-invoice-modal'"
    :ref="'upload-invoice-modal'"
    data-cy="ticket-upload-invoice-modal"
    :modal-class="'has-confirmation-modal'"
    :title="publicTicket ? $t('message.ticketOverview.ticket.uploadInvoice.modal.publicTicketTitle') : $t('message.ticketOverview.ticket.uploadInvoice.modal.title')"
    hide-footer
    no-close-on-backdrop>
    <div
      :class="drag ? 'drag-zone' : ''"
      @dragover="drag = true"
      @drop.prevent="addFile"
      @dragleave.prevent>
      <div
        class="dragover-mask"
        @dragleave="drag=false" />
      <label
        for="file">{{ $t('message.manageDocuments.document') + '*' }}</label>
      <coozzy-form-file-input
        id="file"
        ref="file-input"
        data-cy="ticket-input-invoice-file"
        :disabled="uploading"
        :state="$v.file.$dirty && $v.file.$error ? false : null"
        accept="text/plain, text/csv, application/pdf, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
        @change="addFile" />
      <div
        class="w-100 float-right mt-3">
        <coozzy-button
          data-cy="ticket-upload-invoice-btn"
          :disabled="uploading"
          class="float-right ml-3"
          design="green"
          size="normal"
          @click="uploadClicked">
          {{ $t('message.generic.form.upload') }}
        </coozzy-button>
        <coozzy-button
          v-if="publicTicket && publicTicket.status === 'TICKET_STATUS_IN_PROGRESS'"
          data-cy="ticket-proceed-without-invoice-btn"
          :disabled="uploading"
          class="mr-3"
          design="default"
          size="normal"
          @click="proceedWithoutInvoice">
          {{ $t('message.ticketOverview.ticket.uploadInvoice.modal.proceedWithoutInvoice') }}
        </coozzy-button>
        <coozzy-button
          v-else
          data-cy="ticket-cancel-invoice-btn"
          :disabled="uploading"
          design="default"
          size="normal"
          @click="$bvModal.hide('upload-invoice-modal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import Vue from 'vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput'
import { media } from '@/mixins/media'
import { user } from '@/mixins/user'
import ticketApi from '@/misc/apis/TicketApi'
import * as Sentry from '@sentry/vue'

export default {
  name: 'UploadInvoiceModal',
  components: {
    CoozzyFormFileInput,
    CoozzyButton
  },
  mixins: [media, user],
  props: {
    suffix: {
      type: String,
      default: ''
    },
    ticketId: {
      type: String,
      required: true
    },
    publicTicket: {
      type: Object,
      default: () => null
    },
    operatorName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      drag: false,
      file: null,
      uploading: false,
      modalHiddenHandler: null,
      modalShowHandler: null,
      documentDropHandler: null,
      documentDragoverHandler: null
    }
  },
  computed: {
    isEditor() {
      return this.$store.getters['user/isEditor']
    }
  },
  watch: {
  },
  mounted() {
    this.documentDropHandler = (e) => {
      e.preventDefault()
    }
    this.documentDragoverHandler = (e) => {
      e.preventDefault()
    }
    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'upload-invoice-modal') {
        this.removeDocumentDragListeners()
        this.$v.$reset()
        this.file = null
      }
    }
    this.modalShowHandler = (bvEvent, modalId) => {
      if (modalId === 'upload-invoice-modal') {
        this.addDocumentDragListeners()
      }
    }
    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
    this.$root.$on('bv::modal::show', this.modalShowHandler)
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
    if (this.modalShowHandler) {
      this.$root.$off('bv::modal::show', this.modalShowHandler)
    }
    this.removeDocumentDragListeners()
  },
  methods: {
    addDocumentDragListeners() {
      this.removeDocumentDragListeners()
      document.addEventListener('drop', this.documentDropHandler)
      document.addEventListener('dragover', this.documentDragoverHandler)
    },
    removeDocumentDragListeners() {
      if (this.documentDropHandler) {
        document.removeEventListener('drop', this.documentDropHandler)
      }
      if (this.documentDragoverHandler) {
        document.removeEventListener('dragover', this.documentDragoverHandler)
      }
    },
    show() {
      this.$bvModal.show('upload-invoice-modal')
    },
    hideModal() {
      this.$bvModal.hide('upload-invoice-modal')
      this.$emit('hide-modal')
    },
    proceedWithoutInvoice() {
      this.$bvModal.hide('upload-invoice-modal')
      this.$emit('proceed-without-invoice')
    },
    async uploadClicked() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.uploading = true
        this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
        let media = null
        if (this.file.id) {
          media = this.file
        } else {
          const selectedFile = this.file ? [this.file] : []
          const response = await this.uploadMedia(selectedFile, this.ownerId || '', !!this.publicTicket)
          if (response?.length > 0) {
            media = response[0]
          }
        }
        if (media) {
          // Upload the file to the ticket
          ticketApi.addInvoice(this.ticketId, media.id, this.operatorName, this.publicTicket ? this.publicTicket.publicId : '')
          .then(response => {
            this.uploading = false
            this.reset()
            Vue.toasted.show(this.$t('message.ticketOverview.ticket.uploadInvoice.invoiceAdded'), { type: 'success' })
            this.hideModal()
            if (response?.ticket) {
              this.$emit('uploaded-invoice', response.ticket, !!this.publicTicket)
            } else {
              Sentry.captureException(response, {
                extra: {
                  title: 'response addInvoice'
                }
              })
            }
          }).catch(e => {
              this.uploading = false
              this.handleError(e)
            })
        } else {
          this.uploading = false
        }
      }
    },
    handleError(e) {
      this.reset()
      if (this.addAnotherCheckbox === false) {
        this.hide()
      }
      console.log(e)
      Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
    },
    reset() {
      this.$v.$reset()
      this.uploading = false
      if (this.$refs['file-input']) this.$refs['file-input'].resetPlaceholder()
      this.file = null
    },
    hide() {
      this.$bvModal.hide('upload-invoice-modal')
    },
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files?.length > 0) {
        this.file = files[0]
        this.updateUploadedFileName(this, files[0].name)
      } else {
        if (this.$refs['file-input']) {
          this.$refs['file-input'].resetPlaceholder()
        }
      }
      this.drag = false
    }
  },
  validations() {
    return {
      file: {
        required
      }
    }
  }
}
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 0;
}

.drag-zone .dragover-mask {
  display: block !important;
  transition: z-index 0s step-end;
  z-index: 2500;;
}

.dragover-mask {
  display: none;
  background: rgba(255, 255, 255, 0.3);
  border: 3px dashed $color-primary;
  border-radius: 5px;
  bottom: 0;
  left: 0;
  margin: 2px;
  position: absolute;
  right: 0;
  top: 0;
  transition: z-index .2s step-end;
  z-index: -1;
}
</style>
